import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Popconfirm,
  Space,
  Switch,
  message,
} from 'antd';

// Presentational toggle for the per-org ARM auto-processing pause (plan U8).
// Mirrors AutoArchiveSettings.jsx: the parent (ToggleFeatures) owns the redux
// fetch/update; this component renders the current state and asks the parent to
// persist a change. The toggle is an explicit operator action, so flipping it is
// guarded by a Popconfirm confirmation step before the change is committed.
const settingPaused = (value) => value === true || value === 'true';

const KillSwitchSettings = ({ settings, updateSettings }) => {
  const [paused, setPaused] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const nextSettings = settings || {};
    setPaused(settingPaused(nextSettings.auto_processing_paused));
  }, [settings]);

  const armEnabled = (settings || {}).arm_enabled === true || (settings || {}).arm_enabled === 'true';

  const handleConfirm = async () => {
    if (saving) return;

    const nextPaused = !paused;
    setSaving(true);

    try {
      await updateSettings({ auto_processing_paused: nextPaused });
      setPaused(nextPaused);
      message.success(
        nextPaused
          ? 'ARM auto-processing paused for this organization'
          : 'ARM auto-processing resumed for this organization',
      );
    } catch (error) {
      // Revert any optimistic flip: re-derive paused from the source-of-truth props.
      setPaused(settingPaused((settings || {}).auto_processing_paused));
      const errorMsg = error?.response?.data?.error || 'Failed to update ARM auto-processing';
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (!armEnabled) {
    return (
      <Alert
        type="warning"
        showIcon
        message="ARM is not enabled for this organization. Enable the ARM subscription before pausing or resuming auto-processing."
      />
    );
  }

  const confirmTitle = paused
    ? 'Resume ARM auto-processing for this organization? New referrals will start triggering ARM again.'
    : 'Pause ARM auto-processing for this organization? New referrals will stop triggering ARM, and not-yet-claimed referrals are blocked. (Already-running ARM actions are not interrupted.)';

  return (
    <div>
      <Alert
        type="info"
        showIcon
        message="Pause ARM auto-processing for this organization. Pausing stops new referrals from triggering ARM instantly; it does not remove the ARM subscription or its configuration."
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Space>
          <span>Pause ARM auto-processing:</span>
          <Popconfirm
            title={confirmTitle}
            okText={paused ? 'Resume' : 'Pause'}
            cancelText="Cancel"
            onConfirm={handleConfirm}
            disabled={saving}
          >
            <Switch checked={paused} loading={saving} />
          </Popconfirm>
        </Space>
      </div>
    </div>
  );
};

KillSwitchSettings.defaultProps = {
  settings: {},
};

KillSwitchSettings.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired,
};

export default KillSwitchSettings;
